import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_KIND_LABELS,
  type KindLabels,
  type UnitsPreference,
} from "@heraion/shared";
import { unitWord } from "../weightUnitLabels";

// The ONE place the intensity-kind short labels get resolved against the
// active locale. programDayPanel.ts is pure (no react-i18next), so it takes
// the labels as data; every render path must come through here rather than
// reaching for DEFAULT_KIND_LABELS, or the English default leaks back into
// a user-facing surface (that leak WAS the bug — PE-14: the family saw the
// English-only "HIST" in the metric table and the day-panel summaries, in
// all five locales).
//
// The keys carry the English token as `defaultValue`, so a locale that
// hasn't translated one renders today's label instead of a raw key.
//
// ⚠️ Width contract: composerStyles.ts sizes the metric-table kind column
// on "≤4 canonical characters". A translation longer than 4 chars will
// clip there — keep the per-locale abbreviations short, not spelled out.
// `cellUnit` is the unit the absolute-weight cells are actually expressed in
// (frozen at seed by useProgramEditor), NOT the live profile preference —
// the two can differ while the profile is still loading, and the header has
// to name the unit the numbers underneath are really in.
// ⛔ Required (CN-26 wave 3): an optional `= "kg"` default is exactly the
// type-level defect the wave removes — see units.ts / programEdit.ts.
export function useKindLabels(cellUnit: UnitsPreference): KindLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      // "—" is a glyph, not a word: nothing to translate.
      none: DEFAULT_KIND_LABELS.none,
      // DS-83 follow-up: `unitWord`, the app-wide kg/lb source (CN-26), not
      // the per-callsite `kindShortAbsolute(Lb)` keys this used to read.
      // Same string in all 5 locales, and still within the ≤4-char width
      // contract above ("КГ"/"ФУНТ" included).
      absolute: unitWord(t, cellUnit === "lb" ? "lb" : "kg").toUpperCase(),
      "percent-of-max": t("program.kindShortPercent", {
        defaultValue: DEFAULT_KIND_LABELS["percent-of-max"],
      }),
      history: t("program.kindShortHistory", {
        defaultValue: DEFAULT_KIND_LABELS.history,
      }),
      rpe: t("program.kindShortRpe", {
        defaultValue: DEFAULT_KIND_LABELS.rpe,
      }),
      rir: t("program.kindShortRir", {
        defaultValue: DEFAULT_KIND_LABELS.rir,
      }),
    }),
    [t, cellUnit],
  );
}
